import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  UserPlus,
  Users,
  Mail,
  Phone,
  Calendar,
  Plus,
  Trash2,
  Check,
  Copy,
  Send,
  Star
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const ParentOnboarding = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  
  const [parentForm, setParentForm] = useState({
    name: '',
    email: '',
    phone: '',
    send_welcome_email: true
  });
  
  const [players, setPlayers] = useState([]);
  
  // Trial scheduling state
  const [scheduleTrial, setScheduleTrial] = useState(false);
  const [trialForm, setTrialForm] = useState({
    child_name: '',
    date_of_birth: '',
    skill_level: 'beginner',
    class_id: '',
    trial_date: '',
    send_email: true,
    notes: ''
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, programsRes] = await Promise.all([
          api.get('/classes'),
          api.get('/programs')
        ]);
        setClasses(classesRes.data);
        setPrograms(programsRes.data);
      } catch (error) {
        console.error('Failed to load classes/programs');
      }
    };
    fetchData();
  }, []);
  
  const addPlayer = () => {
    setPlayers([...players, {
      full_name: '',
      date_of_birth: '',
      gender: '',
      skill_level: '',
      medical_notes: ''
    }]);
  };
  
  const updatePlayer = (index, field, value) => {
    const newPlayers = [...players];
    newPlayers[index][field] = value;
    setPlayers(newPlayers);
  };
  
  const removePlayer = (index) => {
    setPlayers(players.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!parentForm.name || !parentForm.email) {
      toast.error('Please fill in parent name and email');
      return;
    }
    
    // Validate trial scheduling if enabled
    if (scheduleTrial) {
      if (!trialForm.child_name || !trialForm.class_id || !trialForm.trial_date) {
        toast.error('Please fill in trial details (child name, class, and date)');
        return;
      }
    }
    
    setLoading(true);
    try {
      // First create the parent account
      const response = await api.post('/admin/onboarding/parent', {
        parent: parentForm,
        players: players.filter(p => p.full_name)
      });
      
      let trialResult = null;
      
      // If trial scheduling is enabled, create the trial
      if (scheduleTrial && response.data.parent_id) {
        try {
          const trialResponse = await api.post('/trial-requests/staff-create', {
            parent_id: response.data.parent_id,
            child_name: trialForm.child_name,
            date_of_birth: trialForm.date_of_birth || null,
            skill_level: trialForm.skill_level,
            class_id: trialForm.class_id,
            trial_date: trialForm.trial_date,
            send_email: trialForm.send_email,
            notes: trialForm.notes || null
          });
          trialResult = trialResponse.data;
        } catch (trialError) {
          console.error('Trial creation failed:', trialError);
          toast.warning('Account created but trial scheduling failed');
        }
      }
      
      setResult({
        ...response.data,
        trial_scheduled: trialResult
      });
      toast.success('Parent account created successfully!');
      
      // Reset form
      setParentForm({ name: '', email: '', phone: '', send_welcome_email: true });
      setPlayers([]);
      setScheduleTrial(false);
      setTrialForm({
        child_name: '',
        date_of_birth: '',
        skill_level: 'beginner',
        class_id: '',
        trial_date: '',
        send_email: true,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  const resetForm = () => {
    setResult(null);
    setParentForm({ name: '', email: '', phone: '', send_welcome_email: true });
    setPlayers([]);
    setScheduleTrial(false);
    setTrialForm({
      child_name: '',
      date_of_birth: '',
      skill_level: 'beginner',
      class_id: '',
      trial_date: '',
      send_email: true,
      notes: ''
    });
  };
  
  const _getClassWithProgram = (classId) => {
    const classDoc = classes.find(c => c.id === classId);
    if (!classDoc) return null;
    const program = programs.find(p => p.id === classDoc.program_id);
    return { ...classDoc, program_name: program?.name };
  };

  return (
    <DashboardLayout title="Parent Onboarding">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="parent-onboarding-page">
        {/* Success Result */}
        {result && (
          <Card className="border-2 border-green-300 bg-green-50">
            <CardHeader className="border-b border-green-200">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-green-800">
                <Check className="h-5 w-5" />
                Account Created Successfully
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-green-700">Parent Name</Label>
                  <p className="font-medium text-green-900">{result.parent_name}</p>
                </div>
                <div>
                  <Label className="text-green-700">Email</Label>
                  <p className="font-medium text-green-900">{result.parent_email}</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <Label className="text-green-700">Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="bg-green-100 px-3 py-2 rounded text-lg font-mono text-green-900">
                    {result.temporary_password}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(result.temporary_password)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  {result.welcome_email_sent 
                    ? '✓ Welcome email sent to parent' 
                    : '⚠ Email not sent - please share credentials manually'}
                </p>
              </div>
              
              {result.players?.length > 0 && (
                <div>
                  <Label className="text-green-700">Players Added ({result.players.length})</Label>
                  <div className="mt-2 space-y-2">
                    {result.players.map((player, i) => (
                      <div key={i} className="bg-white rounded p-3 border border-green-200">
                        <span className="font-medium">{player.full_name}</span>
                        {player.age && <span className="text-green-600 ml-2">({player.age} years old)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {result.trial_scheduled && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <Label className="text-blue-700 flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Trial Class Scheduled
                  </Label>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-blue-600">Child:</span>
                      <span className="font-medium ml-1">{result.trial_scheduled.child_name}</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Date:</span>
                      <span className="font-medium ml-1">{result.trial_scheduled.trial_date}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-blue-600">Class:</span>
                      <span className="font-medium ml-1">{result.trial_scheduled.assigned_class_name}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <Button onClick={resetForm} className="w-full bg-green-600 hover:bg-green-700">
                <UserPlus className="h-4 w-4 mr-2" />
                Create Another Account
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        {!result && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Parent Information */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-blue-500" />
                  Parent Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 lg:p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-1">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={parentForm.name}
                      onChange={(e) => setParentForm({...parentForm, name: e.target.value})}
                      placeholder="John Smith"
                      required
                      data-testid="parent-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-1">
                      <Mail className="h-4 w-4 text-slate-400" />
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={parentForm.email}
                      onChange={(e) => setParentForm({...parentForm, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      data-testid="parent-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-1">
                      <Phone className="h-4 w-4 text-slate-400" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      value={parentForm.phone}
                      onChange={(e) => setParentForm({...parentForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      data-testid="parent-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Options</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Checkbox
                        id="send_email"
                        checked={parentForm.send_welcome_email}
                        onCheckedChange={(checked) => setParentForm({...parentForm, send_welcome_email: checked})}
                        data-testid="send-email-checkbox"
                      />
                      <label htmlFor="send_email" className="text-sm text-slate-600 cursor-pointer">
                        Send welcome email with login credentials
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Players */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-500" />
                    Players (Children)
                  </CardTitle>
                  <Button 
                    type="button" 
                    onClick={addPlayer}
                    variant="outline"
                    size="sm"
                    data-testid="add-player-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Player
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 lg:p-6">
                {players.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No players added yet</p>
                    <p className="text-sm">Click "Add Player" to add children to this account</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {players.map((player, index) => (
                      <div key={index} className="border border-slate-200 rounded-lg p-4 relative" data-testid={`player-form-${index}`}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 text-red-500 hover:bg-red-50"
                          onClick={() => removePlayer(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <h4 className="font-medium text-slate-700 mb-4">Player {index + 1}</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Full Name <span className="text-red-500">*</span></Label>
                            <Input
                              value={player.full_name}
                              onChange={(e) => updatePlayer(index, 'full_name', e.target.value)}
                              placeholder="Child's full name"
                              data-testid={`player-${index}-name`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              Date of Birth
                            </Label>
                            <Input
                              type="date"
                              value={player.date_of_birth}
                              onChange={(e) => updatePlayer(index, 'date_of_birth', e.target.value)}
                              data-testid={`player-${index}-dob`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Gender</Label>
                            <Select
                              value={player.gender}
                              onValueChange={(v) => updatePlayer(index, 'gender', v)}
                            >
                              <SelectTrigger data-testid={`player-${index}-gender`}>
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Skill Level</Label>
                            <Select
                              value={player.skill_level}
                              onValueChange={(v) => updatePlayer(index, 'skill_level', v)}
                            >
                              <SelectTrigger data-testid={`player-${index}-skill`}>
                                <SelectValue placeholder="Select level" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="beginner">Beginner</SelectItem>
                                <SelectItem value="intermediate">Intermediate</SelectItem>
                                <SelectItem value="advanced">Advanced</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="mt-4 space-y-2">
                          <Label>Medical Notes</Label>
                          <Textarea
                            value={player.medical_notes}
                            onChange={(e) => updatePlayer(index, 'medical_notes', e.target.value)}
                            placeholder="Any allergies, conditions, or special requirements..."
                            rows={2}
                            data-testid={`player-${index}-medical`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Schedule Trial Class */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" />
                    Schedule Trial Class
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="schedule_trial"
                      checked={scheduleTrial}
                      onCheckedChange={setScheduleTrial}
                      data-testid="schedule-trial-checkbox"
                    />
                    <label htmlFor="schedule_trial" className="text-sm text-slate-600 cursor-pointer">
                      Schedule a trial class for this parent
                    </label>
                  </div>
                </div>
              </CardHeader>
              {scheduleTrial && (
                <CardContent className="p-4 lg:p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        Child's Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={trialForm.child_name}
                        onChange={(e) => setTrialForm({...trialForm, child_name: e.target.value})}
                        placeholder="Child's name"
                        data-testid="trial-child-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={trialForm.date_of_birth}
                        onChange={(e) => setTrialForm({...trialForm, date_of_birth: e.target.value})}
                        data-testid="trial-dob"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Skill Level</Label>
                      <Select
                        value={trialForm.skill_level}
                        onValueChange={(v) => setTrialForm({...trialForm, skill_level: v})}
                      >
                        <SelectTrigger data-testid="trial-skill-level">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Class <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={trialForm.class_id}
                        onValueChange={(v) => setTrialForm({...trialForm, class_id: v})}
                      >
                        <SelectTrigger data-testid="trial-class-select">
                          <SelectValue placeholder="Select class for trial" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map(cls => {
                            const prog = programs.find(p => p.id === cls.program_id);
                            return (
                              <SelectItem key={cls.id} value={cls.id}>
                                {cls.name} ({prog?.name || 'Program'}) - {cls.schedule_day} {cls.start_time}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        Trial Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={trialForm.trial_date}
                        onChange={(e) => setTrialForm({...trialForm, trial_date: e.target.value})}
                        data-testid="trial-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Notification</Label>
                      <div className="flex items-center gap-2 h-10">
                        <Checkbox
                          id="trial_send_email"
                          checked={trialForm.send_email}
                          onCheckedChange={(checked) => setTrialForm({...trialForm, send_email: checked})}
                          data-testid="trial-send-email"
                        />
                        <label htmlFor="trial_send_email" className="text-sm text-slate-600 cursor-pointer">
                          Send trial confirmation email to parent
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={trialForm.notes}
                      onChange={(e) => setTrialForm({...trialForm, notes: e.target.value})}
                      placeholder="Any additional notes for this trial..."
                      rows={2}
                      data-testid="trial-notes"
                    />
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Submit */}
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-lg"
              data-testid="create-account-btn"
            >
              {loading ? (
                <>Creating Account...</>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Create Account & Send Welcome Email
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParentOnboarding;
